import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import { STATUS_MACHINE_OPTIONS } from "./config/statusMachines";

const STATUS_ENDPOINT =
  "https://lndvjnxrzb.execute-api.us-east-2.amazonaws.com/test/consulta/status/status";

const columns = [
  { field: "id", headerName: "No.", width: 90 },
  { field: "tipoMaquina", headerName: "Tipo Maquina", minWidth: 220, flex: 1 },
  { field: "numeroEquipo", headerName: "ID", minWidth: 140, flex: 1 },
  { field: "dateTime", headerName: "DateTime", minWidth: 220, flex: 1 },
];

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

function normalizeStatusRows(data) {
  return (Array.isArray(data) ? data : []).map((row, index) => ({
    id: row["No."] ?? index + 1,
    tipoMaquina: row["Tipo Maquina"] ?? "",
    numeroEquipo: row["ID"] ?? "",
    dateTime: formatDateTime(row["DateTime"]),
  }));
}

export default function StatusMaquinas() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [tipoMaquina, setTipoMaquina] = useState("all");
  const [idEquipo, setIdEquipo] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState("");

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      fontSize: "0.9rem",
      height: "45px",
      "& fieldset": { borderColor: "#771726" },
      "&:hover fieldset": { borderColor: "#771726" },
      "&.Mui-focused fieldset": { borderColor: "#771726" },
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.85rem",
      color: "#000000",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#000000",
    },
  };

  const handleBuscar = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    try {
      setLoading(true);
      setMensaje("");
      setRows([]);

      const response = await axios.post(
          STATUS_ENDPOINT,
          {
            modulo: "status",
            tipoMaquinaStatus: tipoMaquina,
            idEquipo: idEquipo.trim(),
          }
        );

      let payload = response?.data;

      if (typeof payload?.body === "string") {
        try {
          payload = JSON.parse(payload.body);
        } catch (err) {
          console.error("No se pudo parsear response.data.body:", err);
        }
      }

      const data = payload?.data ?? [];
      const parsed = normalizeStatusRows(data);

      setRows(parsed);

      if (parsed.length === 0) {
        setMensaje("No hay datos que mostrar.");
      }
    } catch (error) {
      console.error("Error status:", error.response ? error.response.data : error.message);
      setMensaje("Error al consultar el status de máquinas.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        maxWidth: "1200px",
        margin: "0 auto",
        paddingTop: "90px",
        paddingX: "10px",
        overflowX: "hidden",
      }}
    >
      {/* Título */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.2,
          mb: 3,
        }}
      >
        <MonitorHeartOutlinedIcon sx={{ color: "#771726", fontSize: 32 }} />
        <Typography sx={{ fontSize: "2rem", fontWeight: 700 }}>
          Status de Máquinas
        </Typography>
      </Box>

      {/* Formulario */}
      <Box
        component="form"
        onSubmit={handleBuscar}
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap={2}
        mb={2}
        justifyContent="center"
        sx={{
          flexWrap: "wrap",
          width: isMobile ? "100%" : "auto",
        }}
      >
        <TextField
          id="status-tipo-maquina"
          name="statusTipoMaquina"
          select
          label="Tipo"
          value={tipoMaquina}
          onChange={(e) => setTipoMaquina(e.target.value)}
          required
          fullWidth={isMobile}
          sx={{
            ...textFieldStyles,
            minWidth: isMobile ? "100%" : 260,
          }}
        >
          {STATUS_MACHINE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          id="status-id-equipo"
          name="statusIdEquipo"
          label="ID Equipo"
          value={idEquipo}
          onChange={(e) => setIdEquipo(e.target.value)}
          fullWidth={isMobile}
          sx={{
            ...textFieldStyles,
            minWidth: isMobile ? "100%" : 220,
          }}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{
            bgcolor: "#771726",
            color: "white",
            width: isMobile ? "100%" : "auto",
            "&:hover": {
              bgcolor: "#747274",
            },
            px: 3,
          }}
        >
          {loading ? "Buscando..." : "Consultar"}
        </Button>
      </Box>

      {/* Mensaje */}
      {mensaje ? (
        <Typography sx={{ width: "100%", mb: 2, textAlign: "left" }}>
          {mensaje}
        </Typography>
      ) : null}

      {/* Tabla */}
      <Box
        mt="20px"
        width="100%"
        sx={{
          paddingBottom: "110px",
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: "#771726",
            color: "#ffffff",
          },
          "& .MuiTablePagination-root": {
            color: "#ffffff",
          },
          "& .MuiSvgIcon-root": {
            color: "#ffffff",
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableColumnMenu
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          slots={{
            loadingOverlay: () => (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                }}
              >
                <CircularProgress sx={{ color: "#4d4a4a" }} />
              </Box>
            ),
          }}
          sx={{
            height: "600px",
            "& .MuiDataGrid-cell": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& .MuiDataGrid-columnHeaders": {
              position: "sticky",
              top: 0,
              backgroundColor: "#771726",
              zIndex: 1,
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#771726",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#ffffff",
              fontWeight: 700,
            },
            "& .MuiDataGrid-sortIcon": {
              color: "#ffffff",
            },
            "& .MuiDataGrid-menuIconButton": {
              color: "#ffffff",
            },
          }}
        />
      </Box>
    </Box>
  );
}